import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Typography, Box, Paper, CircularProgress, FormControl, InputLabel, Select, MenuItem, Button, Alert } from '@mui/material';
import axios from 'axios';

const GooglePropertySelect = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [tokenData, setTokenData] = useState(null);
  const [clientId, setClientId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        setLoading(true);
        
        const urlParams = new URLSearchParams(location.search);
        const sessionId = urlParams.get('session');
        
        if (!sessionId) {
          setError('ID de sessão não encontrado');
          setLoading(false);
          return;
        }
        
        // Buscar dados da sessão
        const response = await axios.get(`/api/google/session/${sessionId}`);
        
        if (response.data.status === 'success') {
          const { clientId, tokenData, properties } = response.data.data;
          setClientId(clientId);
          setTokenData(tokenData);
          setProperties(properties);
          
          if (properties.length === 1) {
            setSelectedProperty(properties[0].id);
          }
        } else {
          setError(response.data.message || 'Erro desconhecido');
        }
      } catch (err) {
        setError(`Falha ao recuperar dados: ${err.response?.data?.message || err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [location]);

  const handlePropertyChange = (event) => {
    setSelectedProperty(event.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedProperty) {
      setError('Selecione uma propriedade para continuar');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Token de autenticação não encontrado');
      }

      const property = properties.find(p => p.id === selectedProperty);
      
      const response = await axios.post(
        `/api/clients/${clientId}/google/connect`,
        {
          propertyId: property.id,
          propertyName: property.name,
          accessToken: tokenData.accessToken,
          refreshToken: tokenData.refreshToken,
          expiresAt: tokenData.expiresAt
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.status === 'success') {
        navigate(`/clients/${clientId}`);
      } else {
        throw new Error(response.data.message || 'Erro desconhecido');
      }
    } catch (err) {
      setError(`Erro ao completar integração: ${err.response?.data?.message || err.message}`);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 5 }}>
        <CircularProgress />
        <Typography>Carregando...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 5 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Selecionar Propriedade do Google Analytics
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        )}
        
        {properties.length === 0 ? (
          <Typography>
            Nenhuma propriedade encontrada. Verifique se você tem acesso ao Google Analytics.
          </Typography>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Propriedade</InputLabel>
              <Select
                value={selectedProperty}
                onChange={handlePropertyChange}
                label="Propriedade"
              >
                {properties.map((property) => (
                  <MenuItem key={property.id} value={property.id}>
                    {property.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/dashboard')}
              >
                Cancelar
              </Button>
              
              <Button 
                variant="contained" 
                onClick={handleSubmit}
                disabled={!selectedProperty || loading}
              >
                Concluir Integração
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default GooglePropertySelect;
